<!-- =========================================================================================
  File Name: DashboardAnalytics.vue
  Description: Dashboard Analytics

========================================================================================== -->

<template>

  <div id="dashboard-home">

    <div class="vx-row mb-6">
      <div class="vx-col lg:w-2/3 w-full">
        <div class="knowledge-base-jumbotron bg-cover rounded-lg flex justify-center items-center h-full">
          <video-background src="https://cdn.discordapp.com/attachments/870391445176086539/870404164193239050/Slowed.mp4" class="rounded-lg"
                            style="min-height: 22em; height: 100%; background-image: url(https://images.unsplash.com/photo-1620121692029-d088224ddc74?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=2089&q=80)">
            <div style="backdrop-filter: blur(55px); height: 100%;"
                 class="knowledge-base-jumbotron-content rounded-lg mb-base p-8 lg:p-12 flex flex-col justify-center items-start">
              <img src="https://connect-cdn.intellectualpoint.com/assets/images/logos/IntellectualPoint.png" class="mb-4" width="190">
              <h1 class="text-white w-full font-bold" style="font-size: 3.4rem">Welcome To Academy</h1>
              <h1 class="text-white w-full font-light" style="font-size: 3rem">by Intellectual Point</h1>
            </div>
          </video-background>
        </div>
      </div>
      <div class="vx-col lg:w-1/3 w-full">
        <div class="grid grid-cols gap-4 h-full">
          <div class="vx-card p-5 flex justify-between items-center gap-4 ">
            <div>
              <p class="font-light">Your Account</p>
              <h1 class="mb-2 font-bold">{{ activeUserInfo.full_name }}</h1>
              <div class="flex flex-wrap gap-2 md:flex-nowrap" >
                <vs-button class="font-semibold" size="small"
                           icon="icon-log-out" icon-pack="feather" color="danger"
                           type="gradient" @click="logout">Logout</vs-button>
                <vs-button class="font-semibold" size="small"
                           icon="icon-sun" icon-pack="feather" color="warning"
                           type="gradient" @click="themeToggleSettings = !themeToggleSettings">Change Theme</vs-button>
              </div>
            </div>
            <img src="https://portal-cdn.rapid.education/profile-pictures/default.jpeg" width="100 " class="rounded-lg hidden lg:block" alt="">
          </div>
          <div class="vx-card p-5 flex justify-between items-center gap-4 ">
            <div>
              <p class="font-light">{{ activeCourseInfo.selected ? 'Current Course' : 'Select Course To Practice'}}</p>
              <h1 class="mb-2 font-bold">{{ activeCourseInfo.name }}</h1>
              <div class="flex flex-wrap gap-2 md:flex-nowrap" v-if="activeCourseInfo.selected">
                <vs-button class="font-semibold" size="small"
                           icon="icon-play" icon-pack="feather" color="success"
                           type="gradient" @click="$router.push('/apps')">Launch Apps</vs-button>
                <vs-button class="font-semibold" size="small"
                           icon="icon-book" icon-pack="feather" color="primary"
                           type="gradient" @click="$router.push('/courses')">Switch Course</vs-button>
              </div>
              <vs-button class="font-semibold" size="small" v-else
                         icon="icon-book" icon-pack="feather" color="primary"
                         type="gradient" @click="$router.push('/courses')">Select Course</vs-button>
            </div>
            <img v-if="activeCourseInfo.selected" :src="activeCourseInfo.thumbnail" width="100 " class="rounded-lg hidden lg:block" alt="">
          </div>
        </div>
      </div>
    </div>

    <div class="vx-row">
      <div class="vx-col w-1/4">
        <div class="vx-card p-5 h-full">
          <div class="flex items-center gap-2 mb-2">
            <feather-icon icon="ToolIcon" svgClasses="h-8 w-8 text-danger"/>
            <h2 class="font-semibold">We're Making Some Changes</h2>
          </div>
          <h6 class="font-light">Expect some changes over the next couple weeks while we upgrade our systems. We're working on your new dashboard. Have an idea, use the feedback tool to submit it to us! Any suggestions are greatly appreciated and valued. </h6>
        </div>
      </div>
      <div class="vx-col w-1/4">
        <div class="vx-card p-5 h-full">
          <div class="flex items-center gap-2 mb-2">
            <feather-icon icon="InfoIcon" svgClasses="h-8 w-8 text-success"/>
            <h2 class="font-semibold">Labs Now Available</h2>
          </div>
          <h6 class="font-light">Its finally here! All Intellectual Point students now have access to labs when practicing CompTIA CySA+ or CompTIA Security+. If your course has labs you can access it using the App Launcher or the Sidebar!</h6>
        </div>
      </div>
      <div class="vx-col w-1/4">
        <div class="vx-card p-5 h-full">
          <div class="flex items-center gap-2 mb-2">
            <feather-icon icon="MessageSquareIcon" svgClasses="h-8 w-8 text-primary"/>
            <h2 class="font-semibold">We Need Your Feedback</h2>
          </div>
          <h6 class="font-light">See something, say something? Have an idea or spot a bug? Report it to us using the new feedback tool at the bottom right of your page. Easily report your issue or provide your suggestions. Your feedback is appreciated.</h6>
        </div>
      </div>
      <div class="vx-col w-1/4">
        <div class="vx-card p-5 h-full">
          <div class="flex items-center gap-2 mb-2">
            <feather-icon icon="AlertTriangleIcon" svgClasses="h-8 w-8 text-warning"/>
            <h2 class="font-semibold">Protect Your Account</h2>
          </div>
          <h6 class="font-light">Never share your account credentials with anyone else, think someone may have gained access to your account? Contact us to secure your account. Intellectual Point staff will never ask for your password.</h6>
        </div>
      </div>
    </div>

    <vs-popup :active.sync="themeToggleSettings" title="Choose Theme">
      <div class="p-2">
        <div class="vx-row mb-4">
          <div class="vx-col w-1/2 text-white cursor-pointer">
            <div class="border border-solid p-6 rounded-lg" style="background-color: #050056" @click="toggleTheme('dusk')" :class="currentTheme === 'dusk' ? 'border-success' : 'border-none'">
              <div class="flex items-center justify-center">
                <feather-icon icon="CloudIcon" svgClasses="h-16 w-16 mr-2"/>
                <h2 class="text-center text-white ml-2">Dusk</h2>
              </div>
            </div>
          </div>
          <div class="vx-col w-1/2 text-white cursor-pointer">
            <div class="border border-solid p-6 rounded-lg" style="background-color: #1B1E22" @click="toggleTheme('dark')" :class="currentTheme === 'dark' ? 'border-success' : 'border-none'">
              <div class="flex items-center justify-center">
                <feather-icon icon="MoonIcon" svgClasses="h-16 w-16 mr-2"/>
                <h2 class="text-center text-white ml-2">Dark</h2>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center">
          <feather-icon icon="InfoIcon" svgClasses="h-6 w-6 mr-2"/>
          <h5 class="font-light">The theme you select will sync to your account</h5>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import AppLauncher from '../platform/apps/launcher/AppLauncher'
import LottieAnimation from '../../components/lottie/LottieAnimation'
import VideoBackground from 'vue-responsive-video-background-player'

export default {
  data () {
    return {
      themeToggleSettings: false
    }
  },
  components: {
    AppLauncher,
    LottieAnimation,
    VideoBackground
  },
  methods: {
    navigateToDetailView () {
      this.$router.push({name: 'app-simulation-starter', params: {simulationId: this.item.id}})
        .catch(() => {
        })
    },
    toggleTheme (color) {
      if (color === 'dark' || color === 'dusk') {
        this.$vs.loading()
        this.$http.post('users/@me/theme', {theme: color})
          .then(response => {
            this.$vs.loading.close()
            localStorage.setItem('selectedTheme', response.data.data.theme)
            this.$store.state.theme = response.data.data.theme
          }).catch(() => { this.$vs.loading.close() })
      }
    },
    logout () {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Logout',
        text: 'Please confirm you want to logout of your accounts.',
        accept: this.confirmLogout,
        acceptText: 'Logout'
      })
    },
    confirmLogout () {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('userInfo')
      this.$router.push('/login')
    }
  },
  computed: {
    activeUserInfo () {
      return this.$store.state.AppActiveUser
    },
    activeCourseInfo () {
      return this.$store.state.AppActiveCourse
    },
    currentTheme () {
      return this.$store.state.theme
    }
  },
  watch: {}
}
</script>
